import {EventInput} from '@fullcalendar/core';
import React from "react";
import FullCalendar from "@fullcalendar/react";
import dayGridPlugin from "@fullcalendar/daygrid";
import interactionPlugin from "@fullcalendar/interaction";
import timeGridPlugin from "@fullcalendar/timegrid";
import { Paper } from "@mui/material";
import './SupportPlanTaskCalendar.scss';
import {SupportSlotDefinitionCustomerAssignmentModel} from '../../../../model/support/slot/support-slot-definition-customer-assignment.model';
import { SupportPlanTask } from "../../../../model/support/task/support-plan-task";

type Props = {
  tasks: SupportPlanTask[];
  slots: SupportSlotDefinitionCustomerAssignmentModel[];
  onEventClick?: (task: SupportPlanTask) => void;
  onSlotClick?: (slot: SupportSlotDefinitionCustomerAssignmentModel, startDate: string, endDate: string) => void
  onCalendarDateRangeChanged: (startDate: string, endDate: string) => void
};

export const mainColors: Record<string, string> = {
  CONSULTATION: "#4e73df",   // niebieski – jak primary button
  ANALYSIS: "#1cc88a",       // zielony – jak secondary button
  GENERAL: "#f6c23e",        // cieplejszy, stonowany fiolet
  LONG_TERM_TASK: "#f6c23e"  // jasny purpurowy, spójny z GENERAL
};

export const subtleColors: Record<string, string> = {
  CONSULTATION: "#a3b5f2",   // jasny niebieski
  ANALYSIS: "#80e0c3",       // jasny zielony
  GENERAL: "#fce6a2",        // pastelowy fiolet
  LONG_TERM_TASK: "#fce6a2"  // jasny, delikatny purpurowy
};

const occupiedSlotColor = "#f87171";

export const SupportPlanCalendar = ({ tasks, slots, onEventClick, onSlotClick, onCalendarDateRangeChanged }: Props) => {

  const generateCalendarEvents = (
    slots: SupportSlotDefinitionCustomerAssignmentModel[],
    tasks: SupportPlanTask[]
  ): EventInput[] => {
    const eventsArray: EventInput[] = [];
    const addedOccupiedTaskIds = new Set<string>();

    // --- Sloty ---
    slots.forEach(slot => {
      const from = new Date(slot.validFrom);
      const to = new Date(slot.validTo);

      for (let d = new Date(from); d <= to; d.setDate(d.getDate() + 1)) {
        const jsWeekday = d.getDay();
        const converted = jsWeekday === 0 ? 7 : jsWeekday;
        if (converted !== slot.weekday) continue;

        const date = d.toISOString().split("T")[0];

        // Szukamy tasków, które idealnie pokrywają slot
        const exactTasks = tasks.filter(task =>
          task.startDate === date &&
          task.endDate === date &&
          task.startTime === slot.startTime &&
          task.endTime === slot.endTime
        );

        if (exactTasks.length > 0) {
          exactTasks.forEach(exactTask => {
            eventsArray.push({
              id: `task-${exactTask.id}-occupied`,
              title: exactTask.title,
              start: `${date}T${slot.startTime}`,
              end: `${date}T${slot.endTime}`,
              allDay: false,
              backgroundColor: occupiedSlotColor,
              borderColor: occupiedSlotColor,
              textColor: "white",
              extendedProps: { slot, task: exactTask, type: "occupied" },
              classNames: ["slot-event"]
            });
            addedOccupiedTaskIds.add(exactTask.id);
          })
        } else {
          eventsArray.push({
            id: `slot-${slot.id}-free-${date}`,
            title: `Wolny slot`,
            start: `${date}T${slot.startTime}`,
            end: `${date}T${slot.endTime}`,
            allDay: false,
            backgroundColor: mainColors[slot.assignmentType],
            borderColor: mainColors[slot.assignmentType],
            textColor: "white",
            extendedProps: { slot, type: "free" },
            classNames: ["slot-event"]
          });
        }
      }
    });

    // --- Eventy niezwiązane ze slotem ---
    tasks.forEach(task => {
      if (addedOccupiedTaskIds.has(task.id)) return; // jeśli już dodany jako zajęty slot, pomijamy

      const isAllDay = !task.startTime || !task.endTime;
      const endDateObj = new Date(task.endDate);
      if (isAllDay) endDateObj.setDate(endDateObj.getDate() + 1);
      const endDateStr = endDateObj.toISOString().split("T")[0];

      eventsArray.push({
        id: task.id,
        title: task.title,
        start: task.startDate + (task.startTime ? `T${task.startTime}` : ""),
        end: isAllDay ? endDateStr : `${endDateStr}T${task.endTime}`,
        allDay: isAllDay,
        backgroundColor: mainColors[task.taskType] ?? "#888",
        borderColor: mainColors[task.taskType] ?? "#888",
        textColor: "white",
        extendedProps: {task: task},
        classNames: ["task-event"]
      });
    });
    return eventsArray;
  };


  const handleEventClick = (info: any) => {
    const task: SupportPlanTask = info.event.extendedProps?.task;
    const slot: SupportSlotDefinitionCustomerAssignmentModel = info.event.extendedProps?.slot

    if(task) {
      onEventClick?.(task)
      return;
    }
    if(slot) {
      const startDateOnly = info.event.start?.toISOString().substring(0, 10);
      const endDateOnly = info.event.end?.toISOString().substring(0, 10);

      onSlotClick?.(slot, startDateOnly, endDateOnly)
      return;
    }
  };

  const formatDateDDMMYYYY = (date: Date) => {
    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0"); // miesiące są 0-indexowane
    const year = date.getFullYear();
    return `${year}-${month}-${day}`;
  };

  return (
    <Paper elevation={2} sx={{ p: 1, borderRadius: "12px" }}>
      <FullCalendar
        plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin]}
        initialView="dayGridMonth"
        headerToolbar={{
          left: "prev,next today",
          center: "title",
          right: ""
        }}
        height="calc(100vh - 300px)"
        events={generateCalendarEvents(slots, tasks)}
        eventClick={handleEventClick}
        firstDay={1}
        eventDisplay="block"
        eventMinHeight={22}
        slotMinTime="08:00:00"
        slotMaxTime="20:00:00"
        datesSet={(dateInfo) => {
          const start = dateInfo.start;
          const end = dateInfo.end;
          console.log(`Nowy zakres kalendarza: ${formatDateDDMMYYYY(start)} - ${formatDateDDMMYYYY(end)}`);

          onCalendarDateRangeChanged(formatDateDDMMYYYY(start), formatDateDDMMYYYY(end))
        }}
      />
    </Paper>
  );
};
